import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Phone, Mail, Globe, Loader2, Search } from "lucide-react";
import ResultCard from "@/components/ResultCard";
import { api, formatApiError } from "@/lib/api";

const EXAMPLES = {
  phone: "+14155552671",
  email: "growth@stripe.com",
  ip: "8.8.8.8",
};

const PLACEHOLDERS = {
  phone: "+1 415 555 2671",
  email: "hello@company.com",
  ip: "104.16.132.229",
};

export default function LookupWidget({ authed = false, defaultTab = "phone" }) {
  const [tab, setTab] = useState(defaultTab);
  const [values, setValues] = useState({ phone: "", email: "", ip: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [latency, setLatency] = useState(0);

  const run = async () => {
    const v = values[tab].trim();
    if (!v) { toast.error("Enter a value to validate"); return; }
    setLoading(true);
    setResult(null);
    const start = Date.now();
    try {
      const endpoint = authed ? `/validate/${tab}` : `/public/validate/${tab}`;
      const { data } = await api.post(endpoint, { value: v });
      setLatency(Date.now() - start);
      setResult({ kind: tab, ...data });
      toast.success(`${tab.toUpperCase()} lookup complete`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const insertExample = () => setValues({ ...values, [tab]: EXAMPLES[tab] });

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur-xl overflow-hidden glow-cyan">
      <Tabs value={tab} onValueChange={(v) => { setTab(v); setResult(null); }}>
        <div className="border-b border-slate-800 bg-slate-950/60 px-2 pt-2">
          <TabsList className="bg-transparent gap-1 h-auto p-0">
            <TabsTrigger
              value="phone"
              data-testid="hero-live-demo-tab-phone"
              className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-signal data-[state=active]:border-cyan-500/30 border border-transparent rounded-t-md rounded-b-none px-4 py-2.5 font-mono text-xs uppercase tracking-widest"
            >
              <Phone className="w-3.5 h-3.5 mr-1.5" /> Phone
            </TabsTrigger>
            <TabsTrigger
              value="email"
              data-testid="hero-live-demo-tab-email"
              className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-signal data-[state=active]:border-cyan-500/30 border border-transparent rounded-t-md rounded-b-none px-4 py-2.5 font-mono text-xs uppercase tracking-widest"
            >
              <Mail className="w-3.5 h-3.5 mr-1.5" /> Email
            </TabsTrigger>
            <TabsTrigger
              value="ip"
              data-testid="hero-live-demo-tab-ip"
              className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-signal data-[state=active]:border-cyan-500/30 border border-transparent rounded-t-md rounded-b-none px-4 py-2.5 font-mono text-xs uppercase tracking-widest"
            >
              <Globe className="w-3.5 h-3.5 mr-1.5" /> IP
            </TabsTrigger>
          </TabsList>
        </div>

        {["phone", "email", "ip"].map((k) => (
          <TabsContent key={k} value={k} className="p-5 mt-0 space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                data-testid={`input-${k}-lookup`}
                value={values[k]}
                onChange={(e) => setValues({ ...values, [k]: e.target.value })}
                placeholder={PLACEHOLDERS[k]}
                onKeyDown={(e) => e.key === "Enter" && run()}
                className="bg-slate-950 border-slate-800 text-slate-100 font-mono placeholder:text-slate-600 focus-visible:ring-cyan-signal focus-visible:ring-offset-0 h-11"
              />
              <Button
                data-testid="button-submit-lookup"
                onClick={run}
                disabled={loading}
                className="bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold h-11 px-6"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                {loading ? "Verifying" : "Verify"}
              </Button>
            </div>
            <div className="flex items-center justify-between text-xs">
              <button
                data-testid={`btn-example-${k}`}
                onClick={insertExample}
                className="text-slate-500 hover:text-cyan-signal font-mono transition-colors"
              >
                → try example: {EXAMPLES[k]}
              </button>
              {!authed && (
                <span className="font-mono text-slate-600 hidden sm:inline">
                  public demo · 10 req/min
                </span>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {result && (
        <div className="border-t border-slate-800 p-5 bg-slate-950/40">
          <ResultCard kind={result.kind} result={result.result} latency={latency} />
        </div>
      )}
    </div>
  );
}
